/**
 * Pages arrondissements Paris (8 pages groupées) — contenu unique par zone.
 * Ajout au projet Readdy pour couvrir le SEO ultra-local parisien.
 */

export interface ArrondissementData {
  slug: string;
  arrs: string;
  nom: string;
  h1: string;
  metaTitle: string;
  metaDesc: string;
  intro: string;
  bati: string;
  diagnostics: string[];
  prixM2: string;
  quartiers: string[];
  heroImage: string;
}

export const arrondissementsData: ArrondissementData[] = [
  {
    slug: "centre",
    arrs: "1er, 2e, 3e, 4e",
    nom: "Paris Centre",
    h1: "Diagnostic immobilier Paris Centre (1er, 2e, 3e, 4e) — Le Marais, Louvre, Châtelet",
    metaTitle: "Diagnostic Immobilier Paris Centre (1-4) | DPE Le Marais | Up Diag",
    metaDesc:
      "Diagnostic immobilier à Paris Centre : 1er, 2e, 3e, 4e arrondissements. DPE, CREP plomb, Pack Vente. Spécialistes du Marais, Louvre, Châtelet. Dès 130 €.",
    intro:
      "Up Diag intervient dans tout le centre de Paris : le Louvre, le Marais, Châtelet, la Bourse et Bastille. Ces arrondissements présentent un parc immobilier essentiellement haussmannien et ancien, avec des spécificités propres aux biens de caractère.",
    bati:
      "Le centre de Paris est dominé par les immeubles antérieurs à 1850 dans le Marais, les immeubles haussmanniens vers l'Opéra et la Bourse, et les constructions XVIIe-XVIIIe siècles rue des Francs-Bourgeois ou rue Saint-Antoine. Les hauteurs sous plafond y sont généralement élevées (3 m à 4 m), ce qui impacte le calcul du DPE.",
    diagnostics: [
      "CREP plomb : quasi-systématique (biens avant 1949)",
      "Amiante : rare (biens anciens peu concernés)",
      "DPE : souvent classes D à F pour les biens non rénovés",
      "Loi Carrez : nombreux lots en copropriété de petite surface",
    ],
    prixM2: "13 500 €/m² en moyenne",
    quartiers: ["Le Marais", "Châtelet - Les Halles", "Louvre - Palais-Royal", "Bastille", "République", "Île de la Cité"],
    heroImage:
      "/img/readdy/arr-centre.jpg",
  },
  {
    slug: "rive-gauche",
    arrs: "5e, 6e, 7e",
    nom: "Paris Rive Gauche",
    h1: "Diagnostic immobilier Paris Rive Gauche (5e, 6e, 7e) — Saint-Germain, Quartier Latin, Invalides",
    metaTitle: "Diagnostic Immobilier Paris Rive Gauche (5-7) | DPE Saint-Germain | Up Diag",
    metaDesc:
      "Diagnostic immobilier Paris 5e, 6e, 7e arrondissements. DPE Saint-Germain, Quartier Latin, Invalides. Audit énergétique pour biens F/G. Dès 130 €.",
    intro:
      "La rive gauche parisienne regroupe les 5e, 6e et 7e arrondissements. Quartiers historiques et prestigieux, ils abritent Saint-Germain-des-Prés, le Quartier Latin, les Invalides et la Tour Eiffel. Un parc immobilier très ancien avec une forte valeur patrimoniale.",
    bati:
      "Le 5e et 6e comptent de nombreux immeubles antérieurs à 1800, tandis que le 7e est majoritairement haussmannien avec des hôtels particuliers XVIIIe-XIXe. Ces biens de prestige nécessitent souvent un Pack Vente complet et un audit énergétique en cas de classification F/G.",
    diagnostics: [
      "CREP plomb systématique",
      "Loi Carrez : obligatoire pour les ventes en copro",
      "DPE : difficile à optimiser (bâti ancien protégé)",
      "Audit énergétique fréquent pour F/G",
    ],
    prixM2: "14 800 €/m² en moyenne",
    quartiers: ["Saint-Germain-des-Prés", "Quartier Latin", "Luxembourg", "Invalides", "Gros-Caillou", "Odéon"],
    heroImage:
      "/img/readdy/arr-rg.jpg",
  },
  {
    slug: "nord-ouest",
    arrs: "8e, 9e, 10e",
    nom: "Paris Nord-Ouest",
    h1: "Diagnostic immobilier Paris 8e, 9e, 10e — Champs-Élysées, Opéra, Gare du Nord",
    metaTitle: "Diagnostic Immobilier Paris 8-10 | DPE Champs-Élysées Opéra | Up Diag",
    metaDesc:
      "Diagnostic immobilier Paris 8e, 9e, 10e arrondissements. DPE, CREP plomb, Pack Vente. Champs-Élysées, Opéra, Gare du Nord. Intervention 48h.",
    intro:
      "Le nord-ouest parisien couvre trois arrondissements contrastés : le 8e et son prestige (Champs-Élysées, Madeleine), le 9e animé autour de l'Opéra, et le 10e avec les gares du Nord et de l'Est. Un secteur à forte rotation immobilière.",
    bati:
      "Le 8e est haussmannien grand standing. Le 9e mixte : haussmanniens, ateliers, petits immeubles de rapport. Le 10e plus populaire avec faubouriens et immeubles 1900-1930. Les diagnostics varient fortement selon la localisation précise.",
    diagnostics: [
      "DPE : fort enjeu énergétique (bâti ancien)",
      "Pack Vente complet pour les copros",
      "CREP plomb obligatoire",
      "Diagnostic commercial fréquent (locaux, boutiques)",
    ],
    prixM2: "11 900 €/m² en moyenne",
    quartiers: ["Champs-Élysées", "Madeleine", "Opéra", "Pigalle", "Canal Saint-Martin", "Gare du Nord"],
    heroImage:
      "/img/readdy/arr-no.jpg",
  },
  {
    slug: "est",
    arrs: "11e, 12e",
    nom: "Paris Est",
    h1: "Diagnostic immobilier Paris 11e & 12e — Bastille, Nation, Bercy",
    metaTitle: "Diagnostic Immobilier Paris 11-12 | DPE Bastille Nation Bercy | Up Diag",
    metaDesc:
      "Diagnostic immobilier Paris Est : 11e et 12e arrondissements. DPE Bastille, République, Nation, Bercy. Pack Vente, CREP plomb. Dès 130 €.",
    intro:
      "L'est parisien concentre le 11e (Bastille, République, Oberkampf) et le 12e (Nation, Bercy, Bois de Vincennes). Des arrondissements vivants avec un parc immobilier varié, souvent plus accessible que le centre historique.",
    bati:
      "Mix entre immeubles 1850-1920, constructions 1930-1960 et résidences récentes (Bercy Village, Nation rénové). Les bâtiments d'ateliers reconvertis sont nombreux. Diagnostics contrastés selon le type de bien.",
    diagnostics: [
      "DPE varié (bons classements sur le bâti récent)",
      "CREP plomb pour les biens anciens",
      "Amiante pour les immeubles 1960-1997",
      "Loi Carrez en copropriété",
    ],
    prixM2: "10 200 €/m² en moyenne",
    quartiers: ["Bastille", "République", "Oberkampf", "Nation", "Bercy", "Bois de Vincennes"],
    heroImage:
      "/img/readdy/arr-est.jpg",
  },
  {
    slug: "sud",
    arrs: "13e, 14e, 15e",
    nom: "Paris Sud",
    h1: "Diagnostic immobilier Paris 13e, 14e, 15e — Montparnasse, Convention, Place d'Italie",
    metaTitle: "Diagnostic Immobilier Paris 13-15 | DPE Montparnasse | Up Diag",
    metaDesc:
      "Diagnostic immobilier Paris Sud : 13e, 14e, 15e arrondissements. DPE collectif copropriétés, amiante, Pack Location. Dès 130 €.",
    intro:
      "Le sud de Paris couvre trois grands arrondissements populaires et familiaux : le 13e (Place d'Italie, BNF), le 14e (Montparnasse, Denfert-Rochereau) et le 15e (Convention, Vaugirard). Nombreux immeubles 1960-1980 et résidences modernes.",
    bati:
      "Beaucoup de copropriétés années 1960-1980 dans le 13e (tours Olympiades, Italie 13) et le 15e (Front de Seine). Ces copropriétés nécessitent souvent le DPE collectif et le DTA amiante. Le 14e mélange haussmanniens et constructions Art Déco.",
    diagnostics: [
      "DPE collectif fréquent (grandes copropriétés)",
      "Amiante parties communes (immeubles 1960-1997)",
      "Pack Location volumineux (zone étudiante)",
      "Loi Carrez / Loi Boutin",
    ],
    prixM2: "9 600 €/m² en moyenne",
    quartiers: ["Place d'Italie", "BNF", "Montparnasse", "Denfert-Rochereau", "Convention", "Front de Seine"],
    heroImage:
      "/img/readdy/arr-sud.jpg",
  },
  {
    slug: "ouest",
    arrs: "16e, 17e",
    nom: "Paris Ouest",
    h1: "Diagnostic immobilier Paris 16e & 17e — Trocadéro, Auteuil, Monceau, Batignolles",
    metaTitle: "Diagnostic Immobilier Paris 16-17 | DPE Trocadéro Auteuil | Up Diag",
    metaDesc:
      "Diagnostic immobilier Paris Ouest : 16e et 17e. DPE haussmannien, audit énergétique F/G, Pack Vente grands biens. Trocadéro, Auteuil, Monceau.",
    intro:
      "L'ouest parisien regroupe le 16e (Trocadéro, Auteuil, Passy, Muette) et le 17e (Monceau, Batignolles, Ternes). Arrondissements résidentiels haut de gamme avec un parc dominé par les haussmanniens et les hôtels particuliers.",
    bati:
      "Le 16e et le 17e présentent une majorité d'immeubles haussmanniens (1860-1920) et Art Nouveau, complétés par des résidences 1930-1950 prestigieuses. Quelques constructions récentes (Batignolles ZAC) offrent des diagnostics plus favorables.",
    diagnostics: [
      "DPE difficile à améliorer (bâti ancien)",
      "CREP plomb systématique",
      "Audit énergétique fréquent (F/G)",
      "Pack Vente complet pour grands biens",
    ],
    prixM2: "12 200 €/m² en moyenne",
    quartiers: ["Trocadéro", "Auteuil", "Passy", "Muette", "Monceau", "Batignolles", "Ternes"],
    heroImage:
      "/img/readdy/arr-ouest.jpg",
  },
  {
    slug: "nord",
    arrs: "18e, 19e, 20e",
    nom: "Paris Nord",
    h1: "Diagnostic immobilier Paris 18e, 19e, 20e — Montmartre, Belleville, Père-Lachaise",
    metaTitle: "Diagnostic Immobilier Paris 18-20 | DPE Montmartre Belleville | Up Diag",
    metaDesc:
      "Diagnostic immobilier Paris Nord : 18e, 19e, 20e. DPE Montmartre, Belleville, Ménilmontant. Rénovation énergétique et CREP plomb. Dès 130 €.",
    intro:
      "Les arrondissements du nord parisien couvrent Montmartre (18e), La Villette et Buttes-Chaumont (19e), et Belleville-Ménilmontant (20e). Un parc immobilier varié et en forte mutation, avec des opportunités locatives et des enjeux énergétiques importants.",
    bati:
      "Majorité d'immeubles faubouriens 1880-1930, quelques haussmanniens, et beaucoup de copropriétés 1960-1980. Les grandes opérations récentes (Paris Nord-Est) amènent des biens neufs avec d'excellents DPE. Zone de rénovation urbaine active.",
    diagnostics: [
      "CREP plomb obligatoire pour les biens anciens",
      "Amiante sur les copros 1960-1997",
      "DPE : enjeu fort (beaucoup de F/G à rénover)",
      "Pack Location fréquent (forte demande locative)",
    ],
    prixM2: "8 700 €/m² en moyenne",
    quartiers: ["Montmartre", "Château Rouge", "La Villette", "Buttes-Chaumont", "Belleville", "Ménilmontant", "Père-Lachaise"],
    heroImage:
      "/img/readdy/arr-nord.jpg",
  },
];
